import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { invalidateWorkerRequirements } from "@src/appV2/redesign/Documents/api/invalidateWorkerRequirements";
import { useQueryClient } from "@tanstack/react-query";
import { type MutableRefObject, useEffect, useRef, useState } from "react";

import { useCompleteCourseEnrollment } from "./api/useCompleteCourseEnrollment";
import { useStartCourseEnrollment } from "./api/useStartCourseEnrollment";
import { type LessonStatus, readScorePercent } from "./scormUtils";
import { type ScormApiRef } from "./useScormApi";

interface UseCourseEnrollmentLifecycleParams {
  courseId: string;
  courseName: string;
  requirementId: string;
  enabled: boolean;
  lessonStatus: LessonStatus;
  scormApiRef: MutableRefObject<ScormApiRef | undefined>;
}

interface UseCourseEnrollmentLifecycleResult {
  courseEnrollmentId: string | undefined;
  resetEnrollment: () => void;
}

export function useCourseEnrollmentLifecycle(
  params: UseCourseEnrollmentLifecycleParams
): UseCourseEnrollmentLifecycleResult {
  const { courseId, courseName, requirementId, enabled, lessonStatus, scormApiRef } = params;

  const [courseEnrollmentId, setCourseEnrollmentId] = useState<string | undefined>();
  const completionSubmittedRef = useRef(false);
  const queryClient = useQueryClient();

  const startCourseEnrollment = useStartCourseEnrollment();
  const completeCourseEnrollment = useCompleteCourseEnrollment();

  const startMutate = startCourseEnrollment.mutate;
  useEffect(() => {
    if (!enabled || isDefined(courseEnrollmentId)) {
      return;
    }

    logEvent(APP_V2_APP_EVENTS.COURSE_STARTED, {
      course_id: courseId,
      requirement_id: requirementId,
    });

    startMutate(
      {
        data: {
          type: "requirement-course-enrollment",
          attributes: { requirementId, courseId, courseTitle: courseName },
        },
      },
      {
        onSuccess: (response) => {
          setCourseEnrollmentId(response.data.id);
        },
      }
    );
  }, [courseEnrollmentId, courseId, courseName, enabled, requirementId, startMutate]);

  const completeMutate = completeCourseEnrollment.mutate;
  useEffect(() => {
    if (lessonStatus === "In Progress" || !isDefined(courseEnrollmentId)) {
      return;
    }

    if (completionSubmittedRef.current) {
      return;
    }

    const scormApi = scormApiRef.current;
    if (!isDefined(scormApi)) {
      return;
    }

    const status: "passed" | "failed" = lessonStatus === "Completed" ? "passed" : "failed";
    const scorePercent =
      readScorePercent({ getValue: scormApi.getValue }) ?? (status === "passed" ? 100 : 0);

    completionSubmittedRef.current = true;

    logEvent(
      status === "passed" ? APP_V2_APP_EVENTS.COURSE_PASSED : APP_V2_APP_EVENTS.COURSE_FAILED,
      { course_id: courseId, requirement_id: requirementId }
    );

    completeMutate(
      {
        courseEnrollmentId,
        request: {
          data: {
            type: "requirement-course-enrollment",
            id: courseEnrollmentId,
            attributes: { status, scorePercent, completionDate: new Date().toISOString() },
          },
        },
      },
      {
        onError: () => {
          completionSubmittedRef.current = false;
        },
        onSettled: () => {
          void invalidateWorkerRequirements(queryClient);
        },
      }
    );
  }, [
    completeMutate,
    courseEnrollmentId,
    courseId,
    lessonStatus,
    queryClient,
    requirementId,
    scormApiRef,
  ]);

  return {
    courseEnrollmentId,
    resetEnrollment: (): void => {
      completionSubmittedRef.current = false;
      setCourseEnrollmentId(undefined);
    },
  };
}
